import * as React from "react";
import { useState, useEffect } from "react";
import axios from "axios";
import { Bell, X, Pause, Database, ExternalLink, Lock, BellOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useNotifications } from "@/context/NotificationContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const NotificationBell = ({ onNavigateToAdmin }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  
  const {
    notifications,
    isLoading,
    hasActiveProblems,
    hasUnread,
    dismissedIds,
    dismissNotification,
    clearDismissed
  } = useNotifications();

  // Check if notifications are enabled
  useEffect(() => {
    const checkConfig = async () => {
      try {
        const response = await axios.get(`${API}/system-config/public`);
        setNotificationsEnabled(response.data.notifications_enabled !== false);
      } catch (error) {
        console.error("Error checking notification config:", error);
      }
    };
    checkConfig();
    // Re-check every 30 seconds
    const interval = setInterval(checkConfig, 30000);
    return () => clearInterval(interval);
  }, []);

  // If notifications are disabled, show disabled bell
  if (!notificationsEnabled) {
    return (
      <Button
        variant="ghost"
        size="icon"
        className="relative opacity-50 cursor-not-allowed"
        disabled
        title="Sistema de notificaciones desactivado"
      >
        <BellOff className="h-5 w-5 text-slate-400" />
      </Button>
    );
  }

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString("es-ES", {
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Get icon based on notification type
  const getNotificationIcon = (notification) => {
    if (notification.icon === "lock") {
      return <Lock className={`h-4 w-4 ${
        notification.type === 'critical' ? 'text-purple-600' : 'text-amber-600'
      }`} />;
    }
    return <Pause className={`h-4 w-4 ${
      notification.type === 'critical' ? 'text-red-600' : 'text-amber-600'
    }`} />;
  };

  // Get icon background color
  const getIconBgColor = (notification) => {
    if (notification.category === "private-space") {
      return 'bg-purple-100';
    }
    return notification.type === 'critical' ? 'bg-red-100' : 'bg-amber-100';
  };

  if (isLoading) {
    return (
      <Button variant="ghost" size="sm" className="relative p-2" disabled>
        <Bell className="h-5 w-5 text-slate-400" />
      </Button>
    );
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="relative p-2 hover:bg-slate-100"
          data-testid="notification-bell"
        >
          <Bell className={`h-5 w-5 ${hasActiveProblems ? 'text-slate-700' : 'text-slate-400'}`} />
          {/* Red dot indicator - shows while problem persists */}
          {hasActiveProblems && (
            <span className="absolute top-1 right-1 flex h-2.5 w-2.5">
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${hasUnread ? 'bg-red-400' : 'bg-amber-400'} opacity-75`}></span>
              <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${hasUnread ? 'bg-red-500' : 'bg-amber-500'}`}></span>
            </span>
          )}
        </Button>
      </PopoverTrigger>
      
      <PopoverContent className="w-96 p-0" align="end">
        {/* Header */}
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Notificaciones
          </h3>
          {hasActiveProblems && (
            <Badge variant="outline" className="text-xs">
              {notifications.length} {notifications.length === 1 ? 'alerta' : 'alertas'}
            </Badge>
          )}
        </div>

        {/* Content */}
        <div className="max-h-80 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="py-8 text-center text-slate-500">
              <Bell className="h-8 w-8 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">Sin notificaciones</p>
              <p className="text-xs text-slate-400 mt-1">
                Todo está funcionando correctamente
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {notifications.map(notification => {
                const isDismissed = dismissedIds.includes(notification.id);
                
                return (
                  <div 
                    key={notification.id}
                    className={`p-4 ${isDismissed ? 'bg-slate-50 opacity-60' : 'bg-white'}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div className={`p-2 rounded-full flex-shrink-0 ${getIconBgColor(notification)}`}>
                        {getNotificationIcon(notification)}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`font-medium text-sm ${
                            notification.type === 'critical'
                              ? 'text-red-800'
                              : 'text-amber-800'
                          }`}>
                            {notification.title}
                          </p>
                          {!isDismissed && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 hover:bg-slate-100"
                              onClick={(e) => {
                                e.stopPropagation();
                                dismissNotification(notification.id);
                              }}
                            >
                              <X className="h-3.5 w-3.5 text-slate-400" />
                            </Button>
                          )}
                        </div>
                        <p className="text-xs text-slate-600 mt-1">
                          {notification.message}
                        </p>
                        
                        {/* Connector info (Fivetran) */}
                        {notification.connector && (
                          <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                            <Database className="h-3 w-3" />
                            <span>{notification.connector.connector_type}</span>
                            <span className="text-slate-300">•</span>
                            <span>{notification.connector.catalog}</span>
                          </div>
                        )}

                        {/* Space info (Private Spaces) */}
                        {notification.space && (
                          <div className="mt-2 p-2 bg-purple-50 rounded text-xs">
                            <p className="text-purple-700">
                              <strong>Solución:</strong> Agregar el usuario de la API al Space en ClickUp
                            </p>
                          </div>
                        )}
                        
                        {/* Time */}
                        <p className="text-xs text-slate-400 mt-2">
                          Detectado: {formatTime(notification.timestamp)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="px-4 py-3 border-t border-slate-100 bg-slate-50">
            <div className="flex items-center justify-between">
              {dismissedIds.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-slate-500 hover:text-slate-700"
                  onClick={clearDismissed}
                >
                  Mostrar todas
                </Button>
              )}
              {onNavigateToAdmin && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-blue-600 hover:text-blue-700 ml-auto gap-1"
                  onClick={() => {
                    setIsOpen(false);
                    onNavigateToAdmin();
                  }}
                >
                  Ver en Admin
                  <ExternalLink className="h-3 w-3" />
                </Button>
              )}
            </div>
            <p className="text-xs text-slate-400 mt-2">
              💡 Las alertas se mantienen mientras el problema persista
            </p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
